import React, { useState, useEffect, useCallback, useMemo } from "react";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutlineOutlined";

import PageContainer from "../layouts/PageContainer";
import CardGridContainer from "../layouts/CardGridContainer";
import PageToolbar from "../components/common/PageToolbar";
import AppSelectFilter from "../components/common/AppSelectFilter";
import BranchSelectFilter from "../components/common/BranchSelectFilter";
import AppButton from "../components/buttons/AppButton";
import ConfirmDialog from "../components/common/ConfirmDialog";

import TimeslotCard from "../components/timeslots/TimeslotCard";
import CreateTimeslotModal from "../components/timeslots/CreateTimeslotModal";
import UpdateTimeslotModal from "../components/timeslots/UpdateTimeslotModal";

import { usePermissions } from "../hooks/usePermissions";
import { useAppDispatch, useAppSelector } from "../redux/store";
import {
  getTimeslots,
  deleteTimeslot,
  resetTimeslotServerStatus,
} from "../redux/actions/timeslotActions";
import { TimeslotResponseDto } from "../shared/dtos/timeslot/TimeslotResponseDto";
import {
  DAYS_OF_WEEK,
  DAY_OF_WEEK_OPTIONS,
} from "../shared/data/calendarConstants";

const TimeslotsPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const { hasPermission } = usePermissions();

  // --- Redux Selectors ---
  const {
    items: timeslots,
    loadingFetchAll: loadingTimeslots,
    errorFetchAll: errorLoadingTimeslots,
    serverStatus,
    loadingCreate: loadingTimeslotCreate,
    errorCreate: errorCreatingTimeslot,
    loadingUpdate: loadingTimeslotUpdate,
    errorUpdate: errorUpdatingTimeslot,
    loadingDelete: loadingTimeslotDelete,
  } = useAppSelector((state) => state.timeslot);

  const { userInfo } = useAppSelector((state) => state.auth);

  // --- Local Filter State ---
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [selectedDay, setSelectedDay] = useState<string>("");
  const [selectedBranchId, setSelectedBranchId] = useState<string>("");

  // --- Modal State ---
  const [isCreateModalOpen, setIsCreateModalOpen] = useState<boolean>(false);
  const [isUpdateModalOpen, setIsUpdateModalOpen] = useState<boolean>(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState<boolean>(false);
  const [selectedTimeslot, setSelectedTimeslot] =
    useState<TimeslotResponseDto | null>(null);

  // --- Permissions ---
  const canCreate = hasPermission("timeslot:create");
  const canUpdate = hasPermission("timeslot:update");
  const canDelete = hasPermission("timeslot:delete");

  // Fetch Data (Optionally passing branchId for super admin)
  const fetchTimeslotsList = useCallback((): void => {
    dispatch(
      getTimeslots(
        selectedBranchId ? { branchId: selectedBranchId } : undefined,
      ),
    );
  }, [dispatch, selectedBranchId]);

  useEffect(() => {
    fetchTimeslotsList();
  }, [fetchTimeslotsList]);

  // Client-side filtering & chronological day sorting
  const filteredTimeslots = useMemo(() => {
    const query = searchTerm.trim().toLowerCase();

    return timeslots
      .filter((ts) => {
        const matchesBranch = selectedBranchId
          ? ts.branchId === selectedBranchId
          : true;
        const matchesDay = selectedDay ? ts.dayOfWeek === selectedDay : true;

        if (!matchesBranch || !matchesDay) return false;
        if (!query) return true;

        const matchesSearch =
          ts.dayOfWeek?.toLowerCase().includes(query) ||
          ts.startTime?.toString().includes(query) ||
          ts.endTime?.toString().includes(query) ||
          ts.branchName?.toLowerCase().includes(query);

        return Boolean(matchesSearch);
      })
      .sort((a, b) => {
        const dayA = a.dayOfWeek ?? "";
        const dayB = b.dayOfWeek ?? "";

        const dayDiff =
          DAYS_OF_WEEK.indexOf(dayA as any) - DAYS_OF_WEEK.indexOf(dayB as any);
        if (dayDiff !== 0) return dayDiff;

        const startA = a.startTime ? String(a.startTime) : "";
        const startB = b.startTime ? String(b.startTime) : "";
        return startA.localeCompare(startB);
      });
  }, [timeslots, selectedBranchId, selectedDay, searchTerm]);

  // --- Modal Handlers ---
  const handleOpenCreate = (): void => setIsCreateModalOpen(true);
  const handleCloseCreate = useCallback((): void => {
    if (!loadingTimeslotCreate) setIsCreateModalOpen(false);
  }, [loadingTimeslotCreate]);

  const handleOpenUpdate = (item: TimeslotResponseDto): void => {
    setSelectedTimeslot(item);
    setIsUpdateModalOpen(true);
  };
  const handleCloseUpdate = useCallback((): void => {
    if (!loadingTimeslotUpdate) {
      setIsUpdateModalOpen(false);
      setSelectedTimeslot(null);
    }
  }, [loadingTimeslotUpdate]);

  const handleOpenDelete = (item: TimeslotResponseDto): void => {
    setSelectedTimeslot(item);
    setIsDeleteModalOpen(true);
  };
  const handleCloseDelete = useCallback((): void => {
    if (!loadingTimeslotDelete) {
      setIsDeleteModalOpen(false);
      setSelectedTimeslot(null);
    }
  }, [loadingTimeslotDelete]);

  const handleDeleteConfirm = (): void => {
    if (selectedTimeslot) {
      dispatch(deleteTimeslot(selectedTimeslot.id));
    }
  };

  // --- Auto-close modals on server status success ---
  useEffect(() => {
    if (!serverStatus) return;
    const { operation, status } = serverStatus;

    if (operation === "create" && status === 201) handleCloseCreate();
    if (operation === "update" && status === 200) handleCloseUpdate();
    if (operation === "delete" && status === 200) handleCloseDelete();

    dispatch(resetTimeslotServerStatus());
  }, [
    serverStatus,
    dispatch,
    handleCloseCreate,
    handleCloseUpdate,
    handleCloseDelete,
  ]);

  return (
    <PageContainer
      title="Class Timeslots"
      subtitle="Manage daily periods and timeslots for classes."
      actions={
        canCreate && (
          <AppButton
            variant="contained"
            startIcon={<AddCircleOutlineIcon />}
            onClick={handleOpenCreate}
          >
            Add Timeslot
          </AppButton>
        )
      }
    >
      <PageToolbar
        searchTerm={searchTerm}
        onSearchChange={(e) => setSearchTerm(e.target.value)}
        searchPlaceholder="Search timeslots (e.g. 08:00, Monday)..."
      >
        {/* Rendered only for SUPER_ADMIN */}
        <BranchSelectFilter
          value={selectedBranchId}
          onChange={(bId) => setSelectedBranchId(bId)}
        />

        <AppSelectFilter
          label="Day of Week"
          value={selectedDay}
          onChange={setSelectedDay}
          options={DAY_OF_WEEK_OPTIONS}
          allLabel="All Days"
        />
      </PageToolbar>

      <CardGridContainer
        loading={loadingTimeslots}
        error={errorLoadingTimeslots}
        itemCount={filteredTimeslots.length}
        emptyMessage="No timeslots match your search criteria"
        onRetry={fetchTimeslotsList}
      >
        {filteredTimeslots.map((ts) => (
          <TimeslotCard
            key={ts.id}
            timeslot={ts}
            onEdit={canUpdate ? () => handleOpenUpdate(ts) : undefined}
            onDelete={canDelete ? () => handleOpenDelete(ts) : undefined}
          />
        ))}
      </CardGridContainer>

      {/* CREATE MODAL */}
      {isCreateModalOpen && (
        <CreateTimeslotModal
          open={isCreateModalOpen}
          onClose={handleCloseCreate}
          userInfo={userInfo}
          loading={loadingTimeslotCreate}
          error={errorCreatingTimeslot}
        />
      )}

      {/* UPDATE MODAL */}
      {isUpdateModalOpen && selectedTimeslot && (
        <UpdateTimeslotModal
          open={isUpdateModalOpen}
          onClose={handleCloseUpdate}
          timeslot={selectedTimeslot}
          userInfo={userInfo}
          loading={loadingTimeslotUpdate}
          error={errorUpdatingTimeslot}
        />
      )}

      {/* DELETE CONFIRM DIALOG */}
      {isDeleteModalOpen && selectedTimeslot && (
        <ConfirmDialog
          open={isDeleteModalOpen}
          onClose={handleCloseDelete}
          onConfirm={handleDeleteConfirm}
          title="Delete Timeslot"
          description={`Are you sure you want to delete the ${selectedTimeslot.dayOfWeek} timeslot (${selectedTimeslot.startTime} - ${selectedTimeslot.endTime})?`}
          loading={loadingTimeslotDelete}
        />
      )}
    </PageContainer>
  );
};

export default TimeslotsPage;
